import { medicareApiClient } from 'clients/medicareApiClient'
import { FC, useCallback, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Button } from 'components/Button/Button'
import { BreadCrumb } from 'components/BreadCrumb/BreadCrumb'
import {
  EMSNDisplay,
  EMSNSearchAndSort,
  RelatedResources,
  UnableToFindClaimAlert,
  UnavailableEMSNsAlert
} from './components'
import {
  defaultEMSNSortForm,
  EMSN,
  EMSNSearchForm,
  EMSNSortForm
} from 'models/eMSN'
import { EMSNPageDescription } from './components/EMSNPageDescription/EMSNPageDescription'
import { LaunchDarklyDependentContent } from 'components/LaunchDarklyDependentContent/LaunchDarklyDependentContent'
import { ContentLayout } from 'components/Layouts'
import { useMedicareUrl } from 'hooks/useMedicareUrl'
import { logAriaMessage } from 'redux/reducers/aria-log'
import { useDispatch } from 'react-redux'
import { MySpinner } from 'components/MySpinner/MySpinner'
import { Alert } from '@cmsgov/design-system'

type RequestState = 'loading' | 'finished' | 'error' | 'idle'

export interface EMSNMessage {
  message: string
}

export const EMSNPage: FC = () => {
  const { t } = useTranslation('eMSNPage')
  const { t: tDmeWarning } = useTranslation('eMSNPageDmeWarning')

  const dispatch = useDispatch()

  // Store if mounted
  const mounted = useRef<boolean>(true)
  useEffect(() => {
    mounted.current = true
    return () => {
      mounted.current = false
    }
  }, [mounted])

  // ************
  // Initial Load
  // ************
  const [eMSNs, setEMSNs] = useState<EMSN[]>([])
  const [requestState, setRequestState] = useState<RequestState>('loading')
  const [signedUpForEMSNs, setSignedUpForEMSNs] = useState<boolean>(true)

  // Initial load
  useEffect(() => {
    let isMounted = true
    const fetchEMSNs = async () => {
      try {
        const emsnResponse = await medicareApiClient.getEMSNs()
        if (isMounted) {
          if (!emsnResponse) {
            throw new Error('eMSN response is undefined')
          }
          if (emsnResponse.preference.CMSMSNMediaPreferenceCode !== 'E') {
            setSignedUpForEMSNs(false)
          } else {
            setSignedUpForEMSNs(true)
          }
          setEMSNs([...emsnResponse.emsns])
          dispatch(logAriaMessage(t('emsn-search.search-complete')))
          setRequestState('finished')
        }
      } catch (error) {
        if (isMounted) {
          dispatch(logAriaMessage(t('emsn-results.emsns-are-unavailable')))
          setRequestState('error')
        }
      }
    }

    // Initialize request state and fetch
    setRequestState('loading')
    fetchEMSNs()

    // Mark component unmounted to prevent memory leaks with calls to dependencies
    return () => {
      isMounted = false
    }
  },
  [
    dispatch,
    setSignedUpForEMSNs,
    setEMSNs,
    setRequestState,
    t
  ])

  // ******
  // Search
  // ******
  const [searchRequestState, setSearchRequestState] = useState<RequestState>('idle')
  const [searchedEMSNs, setSearchedEMSNs] = useState<EMSN[]>([])

  const handleSearchSubmit = useCallback(async (searchCriteria: EMSNSearchForm) => {
    const isSearch = Boolean(searchCriteria.claimNumber)
    try {
      if (isSearch) {
        const emsnResponse = await medicareApiClient.searchEMSNs(searchCriteria)
        if (mounted.current) {
          if (!emsnResponse) {
            throw new Error('eMSN search response is undefined')
          }
          setSearchedEMSNs([...emsnResponse.emsns])
          dispatch(logAriaMessage(t('emsn-search.search-complete')))
          if (emsnResponse.emsns.length === 0) {
            dispatch(logAriaMessage(t('emsn-results.unable-to-find-claim')))
          }
          setSearchRequestState('finished')
        }
      } else {
        const emsnResponse = await medicareApiClient.getEMSNs()
        if (!emsnResponse) {
          throw new Error('eMSN response is undefined')
        }
        if (mounted.current) {
          setSearchedEMSNs([...emsnResponse.emsns])
          dispatch(logAriaMessage(t('emsn-search.search-complete')))
          setSearchRequestState('finished')
        }
      }
    } catch (error) {
      if (mounted.current) {
        dispatch(logAriaMessage(t('emsn-results.emsns-are-unavailable')))
        setSearchRequestState('error')
      }
    }
  }, [
    setSearchedEMSNs,
    setSearchRequestState,
    dispatch,
    mounted,
    t
  ])

  // ****
  // Sort
  // ****
  const [sortCriteria, setSortCriteria] = useState<EMSNSortForm>(defaultEMSNSortForm)
  const handleSortSubmit = (sortCriteriaForm: EMSNSortForm) => {
    setSortCriteria(sortCriteriaForm)
    dispatch(logAriaMessage(t('emsn-sort.sort-complete')))
  }

  // Render helpers
  const isSearching = searchRequestState !== 'idle'
  const initialLoadInProgress = requestState === 'loading' || requestState === 'idle'
  const initialLoadSucceeded = requestState === 'finished'
  const initialLoadFailed = requestState === 'error'
  const searchLoadSucceeded = searchRequestState === 'finished'
  const searchLoadFailed = searchRequestState === 'error'
  const hasInitialEMSNs = eMSNs.length > 0
  const hasSearchedEMSNs = searchedEMSNs.length > 0

  const backLink = useMedicareUrl(
    'https://www.medicare.gov/mbp/claimsearchresult.aspx?Lang=en-us',
    'https://www.medicare.gov/mbp/claimsearchresult.aspx?Lang=es-mx'
  )

  return (
    <>
      <LaunchDarklyDependentContent
        flagName='claimsNewLinkUrlsForClaims'
        fallbackContent={(
          <BreadCrumb
            backButtonText={t('back-to-claims')}
            backButtonLink={backLink}
            navAriaLabel={t('back-to-claims-nav-aria-label')}
            linkIsExternal
            width='wide'
          />
        )}
      >
          <BreadCrumb
            backButtonText={t('back-to-claims')}
            backButtonLink="/claims"
            navAriaLabel={t('back-to-claims-nav-aria-label')}
            width='wide'
          />
      </LaunchDarklyDependentContent>
      <ContentLayout
        heading={t('heading')}
        width='wide'
        withPrintButton
      >
        <>
          <div className='my-u-pb--4'>
            <div className='ds-l-container'>
              {initialLoadSucceeded && signedUpForEMSNs && (
                <LaunchDarklyDependentContent
                  flagName='displayEmsnWarning04122023'
                  fallbackContent={null}
                >
                  <Alert variation='warn' className='my-u-mb--8 my-u-mt--0'>
                    <p>{tDmeWarning('paragraph')}</p>
                  </Alert>
                </LaunchDarklyDependentContent>
              )}
              {initialLoadInProgress && <MySpinner />}
              {initialLoadSucceeded && (
                <div className='my-u-container--8'>
                  <EMSNPageDescription
                    hasInitialEMSNs={hasInitialEMSNs}
                    signedUpForEMSNs={signedUpForEMSNs}
                  />
                </div>
              )}
              {initialLoadSucceeded && hasInitialEMSNs && (
                <EMSNSearchAndSort
                  handleSearchSubmit={handleSearchSubmit}
                  handleSortSubmit={handleSortSubmit}
                  displaySearch={signedUpForEMSNs}
                />
              )}
              {(initialLoadFailed || searchLoadFailed) && <div className='my-u-container--8'><UnavailableEMSNsAlert/></div>}
              {initialLoadSucceeded && !isSearching && hasInitialEMSNs && (
                <EMSNDisplay eMSNs={eMSNs} sortCriteria={sortCriteria} />
              )}
              {searchLoadSucceeded && hasSearchedEMSNs && (
                <EMSNDisplay eMSNs={searchedEMSNs} sortCriteria={sortCriteria} />
              )}
              {searchLoadSucceeded && !hasSearchedEMSNs && (
                <div className='my-u-container--8'><UnableToFindClaimAlert /></div>
              )}
            </div>
          </div>
          {initialLoadSucceeded && hasInitialEMSNs && (
            <div className='ds-l-container'>
              <h2 className='ds-text-heading--xl my-u-pt--40 my-u-m--0'>{t('adobe-help.heading')}</h2>
              <p className='my-u-pt--6'>
                {t('adobe-help.paragraph')}
                <Button href='https://get2.adobe.com/reader/'>{t('adobe-help.download-link')}</Button>
              </p>
            </div>
          )}
          <div className='ds-l-container'>
            <RelatedResources />
          </div>
        </>
      </ContentLayout>
    </>
  )
}
